import Errored from './Errored';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { LANGS } from './utils/constants';
import Tooltip from '@material-ui/core/Tooltip';
import NearMeIcon from '@material-ui/icons/NearMe';
import { makeCancelable, isInOntology } from './utils/utils';
import Avatar from '@material-ui/core/Avatar';
import Chip from '@material-ui/core/Chip';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router-dom';
import { globalStyles as styles } from './style/gstheme';
import withGS from './withGShoc';
import GS from '../grafsync/grafsync';
import AreYouSure from '../core/AreYouSure';
import { getNewTabLink } from '../core/utils/utils';

const loadOptions = {};

class ResourceLink extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            areYouSure: false,
            isErrored: null
        };
        this.cancellable = null;
    }

    doCheck(payload) {
        this.cancellable = makeCancelable(GS.checkForInbound(payload));
        return this.cancellable;
    }

    onSure = () => {
        this.setState({ areYouSure: false });
        if (this.props.onDeleteLink) {
            this.props.onDeleteLink();
        }
        else {
            GS.deleteProperty(this.props.source, this.props.projectGraph, this.props.property, this.props.id);
        }
    };

    onNotSure = () => this.setState({ areYouSure: false });

    handleClick = (event) => {
        const link = this.props.link(this.props.data);
        if (!link && this.props.onClick) {
            this.props.onClick();
            return;
        }
        if (!link) return;
        if (event.ctrlKey) {
            getNewTabLink(link);
            return;
        }
        this.props.history.push(link);
        if (this.props.onLink) this.props.onLink();
    };

    handleDeleteLink = async () => {
        const onDelete = this.props.checkIfUsedOnDelete;
        // We do this directly to ensure direction of link delete is correct 
        if (onDelete) {
            try {
                const isUsed = await this.doCheck({
                    graph: onDelete.graph,
                    value: onDelete.value,
                    predicate: onDelete.predicate
                }).promise;
                if (this.cancellable && this.cancellable.isCancelled()) return;
                if (isUsed) this.setState({ areYouSure: true });
                else this.onSure();
            }
            catch (e) {
                if (this.cancellable && this.cancellable.isCancelled()) return;
                this.setState({ isErrored: e });
            }
        }
        else this.onSure();
    };

    render() {

        const { areYouSure, isErrored } = this.state;

        if (isErrored) return <Errored dataTest="rl-error" data={isErrored} />;

        const { data, classes, defaultLang, isReadOnly, id, context, contextProperty, dataTest, compact, clickable,
            viewLang, ignoreNotAvailable, highlight } = this.props;
        const displayLang = viewLang && data.hasOwnProperty("label") && data.label[viewLang] ?
            viewLang
            :
            data.hasOwnProperty("label") && data.label[defaultLang] ?
                defaultLang
                :
                data.hasOwnProperty("label") && data.label["und"] ?
                    "und"
                    :
                    defaultLang;

        const text = data.hasOwnProperty("label") && data.label[displayLang] ?
            Array.isArray(data.label[displayLang]) ?
                [...data.label[displayLang]].sort().join(" AND ")
                :
                data.label[displayLang]
            :
            id;

        // In this ontology or another?
        const isInContext = context && (data.visibility === context || data[contextProperty]) ?
            isInOntology(data.id, data[contextProperty], context)
            :
            false;
        const isLongLang = displayLang.length > 2;
        const style = isLongLang ?
            {
                width: '40px', borderRadius: '16px',
                ...(viewLang && displayLang !== viewLang && !data.deprecated) ? { backgroundColor: '#FF6F00' } : { backgroundColor: '#01579B' }
            }
            :
            {
                ...(viewLang && displayLang !== viewLang && !data.deprecated) ? { backgroundColor: '#FF6F00' } : { backgroundColor: '#01579B' }
            };

        return (
            <React.Fragment>
                <Chip
                    data-test={dataTest}
                    color="primary"
                    disabled={!clickable && ("isDefinedBy" in data || "visibility" in data)}
                    data-deleted={data.pendingDeletion || data._notAvailable ? true : null}
                    data-deprecated={data.deprecated ? true : null}
                    className={compact ? classes.compactChip : classes.chip}
                    style={(data._notAvailable || data.deprecated) && !ignoreNotAvailable ?
                        { backgroundColor: '#FF6F00' }
                        :
                        data.pendingDeletion ?
                            { backgroundColor: '#DD2C00' }
                            :
                            highlight ?
                                { backgroundColor: '#a0a0a0' }
                                :
                                {}
                    }
                    avatar={
                        (!data.isDefinedBy && !data.visibility)
                            || (data.isDefinedBy && data.isDefinedBy === "http://www.w3.org/2002/07/owl#") ?
                            <Tooltip title="Visit this URL">
                                <Avatar style={{ backgroundColor: 'inherit' }}>
                                    <a href={id} target="_blank" rel="noreferrer">
                                        <OpenInNewIcon fontSize="small" />
                                    </a>
                                </Avatar>
                            </Tooltip>
                            :
                            <Tooltip title={LANGS[displayLang][defaultLang] ?
                                LANGS[displayLang][defaultLang]
                                :
                                LANGS[displayLang]["en"]}>
                                <Avatar data-test={"language-code"} style={style}>{displayLang === "und" ? "None" : displayLang}</Avatar>
                            </Tooltip>
                    }
                    onClick={(this.props.link || this.props.onClick) && !data._notAvailable ? this.handleClick : null}
                    onDelete={!isReadOnly ? (() => this.handleDeleteLink()) : null}
                    label={
                        <Tooltip title={data._notAvailable && !ignoreNotAvailable ?
                            'This has been deleted or is no longer available.'
                            :
                            data.deprecated ?
                                'This has been deprecated'
                                :
                                data.pendingDeletion ?
                                    'This is pending deletion.'
                                    :
                                    <span>
                                        <span style={{ display: 'block', marginTop: 6, marginBottom: 6, marginRight: 50 }}>{data.id}</span>
                                        {(data.domain || data.range) &&
                                            <span style={{ display: 'block', borderTop: 'solid 1px white', paddingTop: 2, marginTop: 6, marginBottom: 6 }}>
                                                {data.domain && <span style={{
                                                    display: 'block',
                                                    fontWeight: 400,
                                                    marginTop: 4
                                                }}>
                                                    <span style={{ display: 'inline-block', fontWeight: 500, width: 50, verticalAlign: 'top' }}>Domain</span>
                                                    <span style={{ display: 'inline-block', width: 'calc(100% - 60px)' }}>{[...data.domain].sort().join(", ")}</span>
                                                </span>}
                                                {data.range && <span style={{
                                                    display: 'block',
                                                    fontWeight: 400,
                                                    marginTop: 4
                                                }}>
                                                    <span style={{ display: 'inline-block', fontWeight: 500, width: 50, verticalAlign: 'top' }}>Range</span>
                                                    <span style={{ display: 'inline-block', width: 'calc(100% - 60px)' }}>{[...data.range].sort()
                                                        .map(d => d.startsWith("http://www.w3.org/2001/XMLSchema#") ?
                                                            "xsd:" + d.substring(33)
                                                            :
                                                            d.startsWith("http://www.w3.org/1999/02/22-rdf-syntax-ns#") ?
                                                                "rdf:" + d.substring(43)
                                                                :
                                                                d)
                                                        .join(", ")}</span>
                                                </span>}
                                            </span>
                                        }
                                    </span>
                        }>
                            {!isInContext ?
                                (!data.isDefinedBy && !data.visibility)
                                    || (data.isDefinedBy && data.isDefinedBy === "http://www.w3.org/2002/07/owl#") ?
                                    <a href={id} target="_new">{data.id}</a>
                                    :
                                    <span data-test="chip-text">
                                        <span data-test="out-of-context" style={{
                                            display: 'inline-block',
                                            transform: 'translateY(2px)'
                                        }}>
                                            <NearMeIcon fontSize="small" />
                                        </span>
                                        <span style={{
                                            display: 'inline-block',
                                            transform: 'translateY(-4px)',
                                            paddingTop: '5px',
                                            paddingLeft: '4px'
                                        }}>
                                            {text}
                                        </span>
                                    </span>
                                :
                                <span data-test="chip-text">{text}</span>
                            }
                        </Tooltip>
                    } />
                {areYouSure && <AreYouSure onSure={this.onSure} onNotSure={this.onNotSure} message={this.props.checkIfUsedOnDelete.message} />}
            </React.Fragment>
        );
    }
}

ResourceLink.defaultProps = {
    isReadOnly: false,
    context: null,
    contextProperty: null,
    ignoreNotAvailable: false,
    compact: false,
    clickable: true,
    highlight: false
};

ResourceLink.propTypes = {
    classes: PropTypes.object,
    history: PropTypes.object,
    highlight: PropTypes.bool,
    dataTest: PropTypes.string,
    ignoreNotAvailable: PropTypes.bool,
    data: PropTypes.object.isRequired,
    clickable: PropTypes.bool,
    id: PropTypes.string.isRequired,
    onDeleteLink: PropTypes.func,
    context: PropTypes.string,
    contextProperty: PropTypes.string,
    link: PropTypes.func,
    onClick: PropTypes.func,
    onLink: PropTypes.func,
    graph: PropTypes.string.isRequired,
    checkIfUsedOnDelete: PropTypes.object,
    projectGraph: PropTypes.string.isRequired,
    source: PropTypes.string,
    isReadOnly: PropTypes.bool,
    property: PropTypes.string,
    compact: PropTypes.bool,
    viewLang: PropTypes.string.isRequired,
    defaultLang: PropTypes.string.isRequired
};

export default withRouter(withStyles(styles)(withGS(ResourceLink, ["ALL"], ["id", "deprecated", "label", "isDefinedBy", "visibility", "type", "domain", "range", "pendingDeletion"], loadOptions)));